import {MATCH_RESULTS, type MatchResult} from "./Constants";
import {getLoggedUser} from "./SecurityUtils";
import type { i18n as I18n } from "i18next";
import type { Ladder, Match, Player } from "../types/domain";
import type { CSSProperties, JSX } from "react";

export const isDefaultWin = (matchState: MatchResult): boolean => {
  return matchState === MATCH_RESULTS.PLAYER_ONE_DEFAULT || matchState === MATCH_RESULTS.PLAYER_TWO_DEFAULT
}

export const isMatchFinished = (matchState: MatchResult): boolean => {
  return isDefaultWin(matchState) || matchState === MATCH_RESULTS.CANCELED ||
    matchState === MATCH_RESULTS.PLAYER_ONE_WON || matchState === MATCH_RESULTS.PLAYER_TWO_WON
}

export const setPlayerElo = (player: Player, ladderId: number): Player => {
  const elo = player.ladders.find( ladder => ladder.ladder.id === ladderId)?.elo
  player.elo = elo ?? 1200
  return player;
}

export const getLadderColor = (ladderId: number): string | undefined => {
  if (ladderId === 1) {
    return "#0DCAF0"
  } else if (ladderId === 2) {
    return "#FFC107"
  } else if (ladderId === 3) {
    return "#DC3545"
  }
  return undefined;
}

export const getLadderName = (i18n: I18n, ladder: Ladder): string => {
  return i18n.language === "cs" ? ladder.name : ladder.nameEn
}

export const getLadderSpan = (i18n: I18n, ladder: Ladder): JSX.Element => {
  const style: CSSProperties = {color: getLadderColor(ladder.id)};
  return <span style={style}>{getLadderName(i18n, ladder)}</span>
}

export const sortMatches = (matches: Match[], filter?: string | null): Match[] => {
  // First, create a stable copy sorted by ID to ensure deterministic ordering
  const stableMatches = [...matches].sort((a, b) => a.id - b.id);

  let sortedMatches = stableMatches.filter(m => !isMatchFinished(m.state)).concat(stableMatches.filter(m => isMatchFinished(m.state)))
  const loggedUser = getLoggedUser()
  if (loggedUser) {
    const myMatchIds = new Set(stableMatches.filter(m => m.playerOne.id === loggedUser.playerId || m.playerTwo.id === loggedUser.playerId).map(m => m.id))
    sortedMatches = sortedMatches
      .filter(m => myMatchIds.has(m.id) && !isMatchFinished(m.state))
      .concat(sortedMatches.filter(m => !myMatchIds.has(m.id) || isMatchFinished(m.state)))
  }
  if (filter) {
    return sortedMatches.filter(match => {
      return (match.playerOne.firstname + " " + match.playerOne.lastname + match.playerOne.nickname +
        match.playerTwo.firstname + " " + match.playerTwo.lastname + match.playerTwo.nickname).toLowerCase().includes(filter.toLowerCase())
    });
  }
  return sortedMatches;
}

export const getRatingColor = (rating: number): string => {
  if (rating < 1) {
    return "#A9A9A9FF"
  } else if (rating < 1.5) {
    return "#198754"
  } else if (rating < 1.75) {
    return "#8e44ad"
  } else if (rating < 2) {
    return "#FFD700"
  } else {
    return "#000000"
  }
}

export type RatingVariant = "GREY" | "DARKGREY" | "success" | "PURPLE" | "GOLD" | "BLACK";

export const getRatingColorVariant = (rating: number, isTournament: boolean = false): RatingVariant => {
  if (rating < 1 && isTournament) {
    return "GREY"
  } else if (rating < 1) {
    return "DARKGREY"
  } else if (rating < 1.5) {
    return "success"
  } else if (rating < 1.75) {
    return "PURPLE"
  } else if (rating < 2) {
    return "GOLD"
  } else {
    return "BLACK"
  }
}

export const getHallOfFameMatchRatingId = (category: string, match: Match): number | null => {
  if (category !== 'HIGHEST_MATCH_RATING') {
    return null;
  }
  if ((match.playerOneRating ?? 0) > (match.playerTwoRating ?? 0)) {
    return match.playerOne.id;
  } else {
    return match.playerTwo.id
  }
}
